import React from 'react';
import styled from 'styled-components';
import { Body5 } from '@ui';
import { CloseButton, FlatButton, TransparentFlatButton } from '@ui/buttons';
import { isPdfDocument } from '@utils/documentVault';
import { DocumentVaultSelectionContext } from '@/contexts/DocumentVaultSelectionContext';
import {
  BULK_ORIGINALS_WARN_THRESHOLD,
  CLEAN_COPY_MAX_SELECTION,
  DownloadQueueContext,
} from '@/contexts/DownloadQueueContext';

// Page width and inset, so the bar's contents line up with the table columns underneath it.
const CONTENT_MAX_WIDTH = 1400;
const CONTENT_INSET = 24;

/*
  Pinned to the viewport rather than the document so the bulk actions stay reachable while
  scrolling a long result set, and left out of the document flow entirely so appearing and
  disappearing never reflows the page. Kept one below the vault's overlay layer (10000: row
  menus, filter dropdowns, the detail drawer) so a popover opened near the top of the screen
  still lands on top of the bar.
*/
const BarWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;

  /* Opaque, and the same surface as the page card it covers. */
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border-left: 3px solid ${({ theme }) => theme.themeColors.primaryEyeCatchingCtaButton};
  border-bottom: 2px solid ${({ theme }) => theme.themeColors.primaryEyeCatchingCtaButton};
  box-shadow: 0 2px 6px 1px rgba(0, 0, 0, 0.08);

  /*
    primaryEyeCatchingCtaButton carries a real accent hue in both themes, unlike
    primaryCtaButton, which is charcoal in Light Professional and would wash the bar grey. The
    tint is painted as its own layer instead of an rgba() mix so tokens that already carry an
    alpha channel still work.
  */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: ${({ theme }) => theme.themeColors.primaryEyeCatchingCtaButton};
    opacity: 0.12;
    pointer-events: none;
  }
`;

const BarContentWrapper = styled.div`
  position: relative;

  max-width: ${CONTENT_MAX_WIDTH}px;
  min-height: 56px;
  margin: 0 auto;
  padding: 8px ${CONTENT_INSET}px;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const SelectionSummaryWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const ClearAffordanceWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 28px;
  height: 28px;
  border-radius: 50%;

  /*
    The shared close icon is an inline svg nested in two plain divs, so its line box carries a few
    px of descender space underneath the glyph -- enough to sit the X above the centre of the
    buttons beside it. Blocking the svg and flattening the divs drops the phantom space.
  */
  div {
    display: flex;
    align-items: center;
  }

  svg {
    display: block;
  }

  &:hover {
    background: ${({ theme }) => theme.themeColors.hoverComponentBackground};
  }
`;

const CountBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 24px;
  height: 24px;
  padding: 0 8px;

  /* Pairs with the primary button beside it rather than introducing a third color. */
  background: ${({ theme }) => theme.themeColors.primaryCtaButton};
  border-radius: 12px;

  /*
    Reaches the children too: bold text sits in a <b>, and BaseText paints its own descendants, so
    a rule on the span alone left the count reading theme text -- charcoal on a charcoal badge in
    Light Professional, i.e. invisible.
  */
  span,
  span * {
    color: ${({ theme }) => theme.themeColors.buttonText};
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// Actions and the dismiss affordance travel together so the X stays pinned to the bar's edge.
const TrailingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CONFIRMATIONS = {
  originals: 'originals',
  cleanCopies: 'cleanCopies',
};

export const SelectionActionBar = () => {
  const { selectedCount, clearSelection, resolveSelectedDocs } = React.useContext(DocumentVaultSelectionContext);
  const { isProcessing, startOriginalsZip, startCleanCopies } = React.useContext(DownloadQueueContext);
  const [pendingConfirmation, setPendingConfirmation] = React.useState(null);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      // Escape backs out of an open confirmation before it gives up the selection, so one stray
      // keypress cannot discard a hard-won set of documents.
      if (pendingConfirmation) {
        setPendingConfirmation(null);
        return;
      }
      clearSelection();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [clearSelection, pendingConfirmation]);

  if (selectedCount === 0) return null;

  const selectedDocs = resolveSelectedDocs();
  const eligibleForCleanCopy = selectedDocs.filter(isPdfDocument);
  const skippedCount = selectedDocs.length - eligibleForCleanCopy.length;
  const isOverCleanCopyCap = eligibleForCleanCopy.length > CLEAN_COPY_MAX_SELECTION;

  const runOriginalsDownload = () => {
    setPendingConfirmation(null);
    startOriginalsZip(selectedDocs);
  };

  const runCleanCopyDownload = () => {
    setPendingConfirmation(null);
    startCleanCopies(eligibleForCleanCopy);
  };

  const handleDownloadOriginals = () => {
    // A large pull is legitimate, but it is also what a stray "select all matching" produces --
    // confirm the count once rather than silently fetching hundreds of files.
    if (selectedCount > BULK_ORIGINALS_WARN_THRESHOLD) {
      setPendingConfirmation(CONFIRMATIONS.originals);
      return;
    }
    runOriginalsDownload();
  };

  const handleDownloadCleanCopies = () => {
    if (isOverCleanCopyCap || eligibleForCleanCopy.length === 0) return;
    // Non-PDFs would come back as untouched passthroughs, so say up front how many are excluded
    // rather than letting the count silently shrink.
    if (skippedCount > 0) {
      setPendingConfirmation(CONFIRMATIONS.cleanCopies);
      return;
    }
    runCleanCopyDownload();
  };

  // The badge and the label are separate elements, so the count and the noun are announced as one
  // phrase rather than as a stray number.
  const summaryLabel = `${selectedCount} ${selectedCount === 1 ? 'document' : 'documents'} selected`;

  return (
    <BarWrapper>
      <BarContentWrapper>
        <SelectionSummaryWrapper role="status" aria-label={summaryLabel}>
          <CountBadge>
            <Body5 bold>{selectedCount}</Body5>
          </CountBadge>

          <Body5 bold>{selectedCount === 1 ? 'document selected' : 'documents selected'}</Body5>
        </SelectionSummaryWrapper>

        <TrailingWrapper>
          {pendingConfirmation === CONFIRMATIONS.originals ? (
            <ActionsWrapper>
              <Body5>Download {selectedCount} original files as a ZIP?</Body5>
              <FlatButton onClick={runOriginalsDownload}>Yes, download</FlatButton>
              <TransparentFlatButton onClick={() => setPendingConfirmation(null)}>Cancel</TransparentFlatButton>
            </ActionsWrapper>
          ) : pendingConfirmation === CONFIRMATIONS.cleanCopies ? (
            <ActionsWrapper>
              <Body5>
                {skippedCount} of {selectedDocs.length} aren’t PDFs; prepare clean copies for the other{' '}
                {eligibleForCleanCopy.length}?
              </Body5>
              <FlatButton onClick={runCleanCopyDownload}>Yes, prepare</FlatButton>
              <TransparentFlatButton onClick={() => setPendingConfirmation(null)}>Cancel</TransparentFlatButton>
            </ActionsWrapper>
          ) : (
            <ActionsWrapper>
              <FlatButton disabled={isProcessing} onClick={isProcessing ? () => {} : handleDownloadOriginals}>
                {isProcessing ? 'Preparing…' : 'Download originals'}
              </FlatButton>

              {eligibleForCleanCopy.length > 0 ? (
                <TransparentFlatButton
                  disabled={isProcessing || isOverCleanCopyCap}
                  onClick={isProcessing ? () => {} : handleDownloadCleanCopies}
                >
                  Download clean copies ({eligibleForCleanCopy.length})
                </TransparentFlatButton>
              ) : null}

              {isOverCleanCopyCap ? (
                <Body5 isLowContrast>
                  Clean copies are prepared one at a time. Select {CLEAN_COPY_MAX_SELECTION} or fewer.
                </Body5>
              ) : null}
            </ActionsWrapper>
          )}

          {/*
            The shared CloseButton renders an unlabeled div, so the name, the role, the key
            handling, and the click all live on the wrapper -- that way the padding around the
            icon is part of the target rather than dead space.
          */}
          <ClearAffordanceWrapper
            role="button"
            tabIndex={0}
            aria-label="Clear selection"
            title="Clear selection"
            onClick={clearSelection}
            onKeyDown={(event) => {
              if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                clearSelection();
              }
            }}
          >
            <CloseButton size={16} />
          </ClearAffordanceWrapper>
        </TrailingWrapper>
      </BarContentWrapper>
    </BarWrapper>
  );
};
